// Settings, Cards and banks: each bank with its cards under it and a status chip on each, derived
// by the server (shared/card-status.ts). A working card is one compact row; tapping it shows its
// details, never the setup steps again. One "Add a card" button opens the add-a-card flow. Below:
// the household's Hush address and the Gmail addresses Hush knows. With nothing set up yet, main.ts
// shows the flow itself instead of this screen.
import type { BankStatusDto, CardStatusDto, OverviewDto } from '../../../shared/api';
import { ATTENTION_REASON, STATUS_CHIP, STATUS_LABEL, type CardStatus } from '../../../shared/card-status';
import { ONE_FILTER_NOTE } from '../../../shared/card-flow';
import { quotedPhrase } from '../../../shared/filter-choice';
import { esc, money } from '../format';
import { detailBar } from './header';
import { mailLine } from './capture';

const when = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

export const chip = (s: CardStatus) => `<span class="stat stat-${s.replace('_', '-')}">${esc(STATUS_CHIP[s])}</span>`;

const copyRow = (id: string, value: string, what: string) =>
  `<div class="addr"><code id="${id}">${esc(value)}</code><button class="btn" data-a="copy" data-from="${id}" data-what="${esc(what)}">Copy</button></div>`;

function cardRow(c: CardStatusDto): string {
  return `<button class="cardrow" data-a="card-open" data-id="${esc(c.id)}"><span class="cname">${esc(c.nickname ?? c.label)}<small>...${esc(c.last4)}</small></span>${chip(c.status)}</button>`;
}

function bankBlock(b: BankStatusDto): string {
  const cards = b.cards.length ? b.cards.map(cardRow).join('') : `<p class="hint cnone">${esc(STATUS_LABEL[b.status])}. No card number seen yet.</p>`;
  // Several cards at a Working bank: say once that the filter is per bank and alerts are per card.
  const note = b.status === 'working' && b.cards.length > 1 ? `<p class="hint bnote">${esc(ONE_FILTER_NOTE(b.name))}</p>` : '';
  return `<section class="bank"><button class="bhead" data-a="bank-open" data-key="${esc(b.key)}"><span>${esc(b.name)}</span>${chip(b.status)}</button>${note}<div class="list">${cards}</div></section>`;
}

/** The screen. */
export function vCardsBanks(d: OverviewDto): string {
  const cb = d.cardsBanks;
  const gmail = cb.gmail.length
    ? `<div class="list">${cb.gmail
        .map(
          (g) =>
            `<div class="brow"><span class="sender">${esc(g.address)}</span><span class="stat ${g.verified ? 'stat-working' : 'stat-waiting'}">${g.verified ? 'Verified' : 'Not verified yet'}</span></div>`,
        )
        .join('')}</div>`
    : '<p class="hint">Hush learns your Gmail address when you add your first card.</p>';
  const recent = d.recentMail.length
    ? `<details class="more"><summary>Recent forwarded mail</summary><ul class="mail">${d.recentMail
        .map((m) => `<li><span>${esc(mailLine(m))}</span><small>${when(m.receivedAt)}</small></li>`)
        .join('')}</ul></details>`
    : '';
  return `${detailBar('Cards and banks', cb.summary)}
  ${cb.banks.map(bankBlock).join('')}
  <div class="btns"><button class="btn primary" data-a="flow-open">Add a card</button></div>
  <h2>Email addresses</h2>
  <p class="hint">Your Hush address</p>${copyRow('hush-addr', cb.forwardAddress, 'Address')}
  <p class="hint">Gmail addresses that forward to Hush</p>${gmail}
  <details class="more"><summary>Add a Gmail address by hand</summary>
  <div class="addcat"><input id="my-address" type="email" autocomplete="email" placeholder="you@gmail.com" aria-label="Your Gmail address"><button class="btn" data-a="my-address-add">Add</button></div></details>
  ${recent}`;
}

/** A card's details (a sheet): status, why, last alert, rename. No setup steps. */
export function vCardSheet(d: OverviewDto, id: string): string {
  const bank = d.cardsBanks.banks.find((b) => b.cards.some((c) => c.id === id));
  const c = bank?.cards.find((x) => x.id === id);
  if (!bank || !c) return '';
  const reason = c.status === 'attention' && c.reason ? `<p class="hint">${esc(ATTENTION_REASON[c.reason])}</p>` : '';
  return `<div class="grab"></div><h2>${esc(c.nickname ?? c.label)} <small>...${esc(c.last4)}</small></h2>
  <p>${chip(c.status)} ${esc(bank.name)}</p>${reason}
  <p class="hint">${c.lastAlertAt ? `Last alert through your filter: ${esc(when(c.lastAlertAt))}.` : 'No alert through your filter yet.'}</p>
  <input type="hidden" id="c-id" value="${esc(c.id)}">
  <label class="fld"><span>Name</span><input id="c-name" type="text" maxlength="40" placeholder="${esc(`${bank.name} ...${c.last4}`)}" value="${esc(c.nickname ?? '')}"></label>
  <div class="btns"><button class="btn primary" data-a="card-save">Save</button><button class="btn" data-a="close">Close</button></div>`;
}

/** A bank's details (a sheet): its filter, the lowest alert amount, rename and remove. */
export function vBankSheet(d: OverviewDto, key: string): string {
  const b = d.cardsBanks.banks.find((x) => x.key === key);
  if (!b) return '';
  const reason = b.status === 'attention' && b.reason ? `<p class="hint">${esc(ATTENTION_REASON[b.reason])}</p>` : '';
  const filter = b.sender
    ? `<div class="field"><span>From</span>${copyRow('bk-from', b.sender, 'From')}</div>${
        b.filterWords
          ? `<div class="field"><span>Has the words</span>${copyRow('bk-words', quotedPhrase(b.filterWords), 'Has the words')}</div>`
          : b.subjectPattern
            ? `<div class="field"><span>Subject</span>${copyRow('bk-subject', b.subjectPattern, 'Subject')}</div>`
            : ''
      }${b.gmail ? `<p class="hint">Filter in ${esc(b.gmail)}.</p>` : ''}`
    : '';
  const thr = b.sender
    ? `<label class="fld"><span>Lowest purchase this bank emails about (0 means every purchase)</span><input data-threshold="${esc(b.sender)}" inputmode="decimal" placeholder="Not set" value="${b.thresholdCents === null ? '' : b.thresholdCents / 100}"></label>${
        b.thresholdCents ? `<p class="hint">Purchases under ${money(b.thresholdCents, 2)} send no alert. Add those with the + button.</p>` : ''
      }`
    : '';
  const manage = b.id
    ? `<label class="fld"><span>Name shown in Hush</span><input type="text" maxlength="60" data-rename="${esc(b.id)}" value="${esc(b.name)}"></label>
  <div class="btns"><button class="btn" data-a="bank-remove" data-id="${esc(b.id)}">Remove bank</button><button class="btn" data-a="close">Close</button></div>`
    : b.sender && b.status !== 'not_connected'
      ? `<div class="btns"><button class="btn" data-a="sender-remove" data-sender="${esc(b.sender)}">Remove</button><button class="btn" data-a="close">Close</button></div>`
      : `<div class="btns"><button class="btn" data-a="close">Close</button></div>`;
  return `<div class="grab"></div><h2>${esc(b.name)}</h2><p>${chip(b.status)}</p>${reason}${filter}${thr}${manage}`;
}

/** True when there is nothing yet: the screen is the add-a-card flow itself. */
export const nothingSetUp = (d: OverviewDto) => !d.cardsBanks.banks.length;
